import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calculator } from "lucide-react";

import Stepper from "@/components/calculator/Stepper";
import Step1 from "@/components/calculator/Step1";
import Step2 from "@/components/calculator/Step2";
import Step3 from "@/components/calculator/Step3";

import { SERVICES, EXTRAS, FIXED_EXTRAS, COMPANY, PRICE_LEVELS } from "@/data/services";
import { computeEstimate, formatEUR } from "@/lib/calc";
import { exportPublicEstimatePDF } from "@/lib/public-pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  step: 1,
  serviceId: null,
  itemId: null,
  quantity: "",
  priceLevelId: "recomendado",
  selectedExtras: [],
  selectedFixed: [],
  notes: "",
  clientName: "",
  clientPhone: "",
  clientZone: "",
  propertyType: "",
  timing: "",
};

export default function CalculatorSection() {
  const [step, setStep] = useState(INITIAL.step);
  const [serviceId, setServiceId] = useState(INITIAL.serviceId);
  const [itemId, setItemId] = useState(INITIAL.itemId);
  const [quantity, setQuantity] = useState(INITIAL.quantity);
  const [priceLevelId, setPriceLevelId] = useState(INITIAL.priceLevelId);
  const [selectedExtras, setSelectedExtras] = useState(INITIAL.selectedExtras);
  const [selectedFixed, setSelectedFixed] = useState(INITIAL.selectedFixed);
  const [notes, setNotes] = useState(INITIAL.notes);
  const [clientName, setClientName] = useState(INITIAL.clientName);
  const [clientPhone, setClientPhone] = useState(INITIAL.clientPhone);
  const [clientZone, setClientZone] = useState(INITIAL.clientZone);
  const [propertyType, setPropertyType] = useState(INITIAL.propertyType);
  const [timing, setTiming] = useState(INITIAL.timing);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const resultRef = useRef(null);
  const topRef = useRef(null);

  const service = SERVICES.find(s => s.id === serviceId);
  const item = service?.items.find(i => i.id === itemId);

  const estimate = useMemo(
    () => computeEstimate({ item, quantity, priceLevelId, selectedExtras, selectedFixed }),
    [item, quantity, priceLevelId, selectedExtras, selectedFixed]
  );

  const canGoNext = step === 1 ? !!item : step === 2 ? Number(quantity) > 0 : true;

  // Escuchar eventos desde la grid de servicios y NeedHelper
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      // detail puede ser un string (id) o un objeto {id, itemId}
      const sid = typeof detail === 'string' ? detail : detail?.id;
      const iid = typeof detail === 'object' ? detail?.itemId : null;
      if (!sid) return;
      setStep(1);
      setServiceId(sid);
      setItemId(iid || null);
      if (iid) {
        const svc = SERVICES.find(s => s.id === sid);
        const it = svc?.items.find(i => i.id === iid);
        if (it && (it.unit === 'ud' || it.unit === 'punto')) setQuantity(1);
        else setQuantity("");
      }
    };
    window.addEventListener("ps24-select-service", handler);
    return () => window.removeEventListener("ps24-select-service", handler);
  }, []);

  const handleSelectService = (sid, iid) => {
    setServiceId(sid);
    setItemId(iid);
    // Si la unidad es trabajo/ud/punto, valor por defecto 1; m²/ml → vacío
    if (iid) {
      const svc = SERVICES.find(s => s.id === sid);
      const it = svc?.items.find(i => i.id === iid);
      if (it && (it.unit === 'ud' || it.unit === 'punto')) {
        setQuantity(1);
      } else {
        setQuantity("");
      }
    }
  };

  const handleReset = () => {
    setStep(1);
    setServiceId(null);
    setItemId(null);
    setQuantity(INITIAL.quantity);
    setPriceLevelId(INITIAL.priceLevelId);
    setSelectedExtras([]);
    setSelectedFixed([]);
    setNotes("");
    setClientName("");
    setClientPhone("");
    setClientZone("");
    setPropertyType("");
    setTiming("");
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleNext = () => {
    const nextStep = Math.min(3, step + 1);
    setStep(nextStep);
    if (nextStep === 3) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const handleRequest = async () => {
    if (!item) return;
    setSubmitting(true);
    const level = PRICE_LEVELS.find(p => p.id === priceLevelId);
    const extrasLabels = EXTRAS.filter(e => selectedExtras.includes(e.id)).map(e => e.label);
    const fixedLabels = FIXED_EXTRAS.filter(f => selectedFixed.includes(f.id)).map(f => `${f.label} (${f.amount}€)`);

    try {
      await axios.post(`${API}/leads`, {
        name: clientName || null,
        phone: clientPhone || null,
        service_category: service.name,
        service_item: item.name,
        quantity: Number(quantity),
        unit: item.unit,
        price_level: priceLevelId,
        extras: selectedExtras,
        extra_costs: Object.fromEntries(FIXED_EXTRAS.filter(f => selectedFixed.includes(f.id)).map(f => [f.id, f.amount])),
        notes: [
          clientZone ? `Zona: ${clientZone}` : null,
          propertyType ? `Tipo: ${propertyType}` : null,
          timing ? `Plazo: ${timing}` : null,
          notes,
        ].filter(Boolean).join(' | ') || null,
        estimated_total: estimate.total,
      });
    } catch (e) {
      console.warn("Lead save failed", e?.message);
    }

    const lines = [
      `Hola, he realizado una estimación en la calculadora de ${COMPANY.name} y quiero solicitar una visita técnica para confirmar el presupuesto.`,
      ``,
      `Servicio: ${item.name}`,
      `Medición: ${quantity} ${item.unit}`,
      `Precio base: ${item.price} €/${item.unit}`,
      `Subtotal: ${formatEUR(item.price * Number(quantity))}`,
      `Nivel: ${level.label} (×${level.factor.toFixed(2)})`,
      `Suplementos: ${extrasLabels.length ? extrasLabels.join(', ') : 'Ninguno'}`,
      fixedLabels.length ? `Conceptos adicionales: ${fixedLabels.join(', ')}` : null,
      `Total estimado: ${formatEUR(estimate.total)} (IVA no incluido)`,
      ``,
      clientName ? `Nombre: ${clientName}` : null,
      clientPhone ? `Teléfono: ${clientPhone}` : null,
      clientZone ? `Zona de la obra: ${clientZone}` : null,
      propertyType ? `Tipo: ${propertyType}` : null,
      timing ? `Plazo deseado: ${timing}` : null,
      notes ? `Comentarios: ${notes}` : null,
    ].filter(Boolean);

    const url = `https://wa.me/${COMPANY.whatsappIntl}?text=${encodeURIComponent(lines.join('\n'))}`;
    toast.success("Abriendo WhatsApp con tu solicitud…");
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      setSubmitting(false);
    }, 400);
  };

  return (
    <section id="calculadora" ref={topRef} className="relative bg-slate-50/60 border-y border-slate-200 scroll-mt-20">
      <div className="container mx-auto px-4 md:px-8 py-16 md:py-24">
        {/* Pre-text explicativo */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold mb-3">Herramienta de presupuesto</div>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Calcula una estimación orientativa para tu reforma
          </h2>
          <p className="text-slate-600 mt-3 text-sm md:text-base leading-relaxed">
            Con esta herramienta puedes obtener una idea aproximada del coste de tu trabajo según el tipo de servicio, medición y dificultad.
          </p>
          <p className="text-slate-500 mt-2 text-sm leading-relaxed">
            El precio mostrado es orientativo y sirve como referencia inicial. El presupuesto definitivo se confirmará tras visita técnica, medición real y revisión del estado de la obra.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form panel */}
          <div className="lg:col-span-8">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-10">
              <Stepper current={step} />
              <div className="mt-10" ref={step === 3 ? resultRef : null}>
                {step === 1 && (
                  <Step1
                    selectedServiceId={serviceId}
                    selectedItemId={itemId}
                    onSelect={handleSelectService}
                  />
                )}
                {step === 2 && (
                  <Step2
                    item={item}
                    quantity={quantity} setQuantity={setQuantity}
                    priceLevelId={priceLevelId} setPriceLevelId={setPriceLevelId}
                    selectedExtras={selectedExtras} setSelectedExtras={setSelectedExtras}
                    selectedFixed={selectedFixed} setSelectedFixed={setSelectedFixed}
                    notes={notes} setNotes={setNotes}
                    clientName={clientName} setClientName={setClientName}
                    clientPhone={clientPhone} setClientPhone={setClientPhone}
                    clientZone={clientZone} setClientZone={setClientZone}
                    propertyType={propertyType} setPropertyType={setPropertyType}
                    timing={timing} setTiming={setTiming}
                  />
                )}
                {step === 3 && (
                  <Step3
                    item={item}
                    quantity={quantity}
                    priceLevelId={priceLevelId}
                    selectedExtras={selectedExtras}
                    selectedFixed={selectedFixed}
                    estimate={estimate}
                    notes={notes}
                    propertyType={propertyType}
                    timing={timing}
                    onRequest={handleRequest}
                    onReset={handleReset}
                    onExportPDF={async () => {
                      if (!item) return;
                      setExporting(true);
                      toast.message("Generando PDF…");
                      try {
                        await exportPublicEstimatePDF({
                          serviceCategory: service.name,
                          item,
                          quantity: Number(quantity),
                          priceLevelId,
                          estimate,
                          selectedExtras,
                          selectedFixed,
                          clientName,
                          clientPhone,
                          clientZone,
                          propertyType,
                          timing,
                          notes,
                        });
                        toast.success("PDF descargado correctamente");
                      } catch (e) {
                        toast.error("Error generando el PDF");
                        console.error(e);
                      } finally {
                        setExporting(false);
                      }
                    }}
                    submitting={submitting}
                    exporting={exporting}
                  />
                )}
              </div>

              {step < 3 && (
                <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(s => Math.max(1, s - 1))}
                    disabled={step === 1}
                    data-testid="step-back-btn"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Anterior
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canGoNext}
                    data-testid="step-next-btn"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-[#004B87] hover:bg-[#003A6A] disabled:opacity-50 text-white text-sm font-medium transition-colors"
                  >
                    {step === 1 ? <>Continuar con detalles <ArrowRight className="w-4 h-4" /></> : <><Calculator className="w-4 h-4" /> Calcular presupuesto</>}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Live total sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-4">
              <div
                className="rounded-2xl bg-[#0A192F] text-white p-6 md:p-7 shadow-[0_20px_60px_-20px_rgba(0,75,135,0.45)]"
                data-testid="live-total-card"
              >
                <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-blue-200">Presupuesto Total Estimado</div>
                <div className="font-display font-bold text-4xl md:text-5xl leading-none mt-4 tracking-tighter tabular-nums" data-testid="live-total-amount">
                  {formatEUR(estimate.total)}
                </div>
                <div className="text-blue-100/90 text-xs mt-3">
                  {item ? `${item.name}` : "Selecciona un servicio para calcular"}
                </div>
                <div className="text-blue-200/70 text-[11px] mt-1">IVA no incluido · Estimación orientativa</div>
                {estimate.minApplied && (
                  <div className="mt-3 inline-block px-2 py-0.5 rounded bg-white/10 text-[11px]">
                    Intervención mínima aplicada (180€)
                  </div>
                )}
                <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 text-[11px]">
                  <MiniStat label="Base" value={formatEUR(estimate.base)} />
                  <MiniStat label="Suplementos" value={formatEUR(estimate.extrasAmount)} />
                  <MiniStat label="Conceptos" value={formatEUR(estimate.fixedAmount)} />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold mb-2">Por qué confiar</div>
                <ul className="space-y-1.5 text-slate-700 text-[13px]">
                  <li>· Empresa S.L.U. legalmente registrada</li>
                  <li>· Equipo propio, personal asegurado</li>
                  <li>· Garantía de obra y trato directo</li>
                  <li>· Visita técnica en tu domicilio</li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-blue-200/70 uppercase tracking-wider">{label}</div>
      <div className="font-display font-semibold text-white mt-1 tabular-nums">{value}</div>
    </div>
  );
}
